import * as React from 'react';
import { useRecoilValue } from 'recoil';
import { AEventTypes } from '../../../atoms/global/events';
import { AUsers } from '../../../atoms/global/users';
import Dropdown from '../../../components_v2/dropdown/Dropdown';
import DropdownTagsCloud from '../../../components_v2/dropdown/DropdownTagsCloud';
import { DefaultTextDiv } from '../../../styles/global/css/GlobalText';
import { DarkGreySidely2 } from '../../../styles/global/css/Utils';
import { translateToString } from '../../../styles/global/translate';
import { FlexDiv } from '../../products/style';
import { getTeams, Team } from '../../teams/actions';
import { OpeningHoursRule, OpeningHoursRuleMode } from './actions';

// The opening-hours check of an hours column, edited from the column's
// settings popup: whether an event outside the company's hours of this
// column is let through, warned about or blocked, for which event types,
// teams and users (none selected = all). It saves with the column's
// display options; the server reads it at event creation and edition.

const MODES: OpeningHoursRuleMode[] = ['off', 'warn', 'block'];
const NO_RULE: OpeningHoursRule = { mode: 'off', event_type_scope: null, user_scope: null, team_scope: null };

function ScopeCloud(props: { label: string, none: string, tags: { id: number, name: string }[], selected: number[] | null, onChange: (ids: number[] | null) => void }) {
	const selected = props.selected ?? [];
	return <FlexDiv flow='column' align='stretch' gap='8px'>
		<label style={{ fontSize: '12px', color: DarkGreySidely2 }}>{props.label}</label>
		<DropdownTagsCloud
			addImageSize='1.25em'
			NoneContainer={<DefaultTextDiv>{props.none}</DefaultTextDiv>}
			horizontal
			addImageOnHover
			dropdownSearch
			selected={props.tags.filter(tag => selected.includes(tag.id))}
			onChange={values => props.onChange(values.length > 0 ? values.map(value => value.id) : null)}
			onDelete={id => {
				const rest = selected.filter(selectedId => selectedId !== id);
				props.onChange(rest.length > 0 ? rest : null);
			}}
			tags={props.tags}
		/>
	</FlexDiv>;
}

export default function OpeningHoursRuleEditor(props: { rule: OpeningHoursRule | undefined, onChange: (rule: OpeningHoursRule) => void }) {
	const rule = props.rule ?? NO_RULE;
	const eventTypes = useRecoilValue(AEventTypes);
	const eventTypeTags = React.useMemo(() => eventTypes.map(type => ({ id: type.id, name: translateToString(`event.${type.name}`) })), [eventTypes]);
	const users = useRecoilValue(AUsers);
	const activeUsers = React.useMemo(() => users.filter(user => user.status).map(user => ({ id: user.id, name: user.name })), [users]);
	const [teams, setTeams] = React.useState<Team[]>([]);
	const modeOptions = React.useMemo(() => MODES.map(mode => ({ label: translateToString(`opening_hours_rule.mode_${mode}`), value: mode })), []);

	React.useEffect(() => {
		getTeams().then(setTeams).catch(console.error);
	}, []);

	const set = (patch: Partial<OpeningHoursRule>) => props.onChange({ ...rule, ...patch });

	return <FlexDiv flow='column' align='stretch' gap='1.5em' margin='0.5em 0 0 0'>
		<Dropdown<OpeningHoursRuleMode>
			name='DD-opening-hours-rule-mode'
			label={translateToString('opening_hours_rule.mode')}
			datalist={modeOptions}
			selectedValue={modeOptions.find(option => option.value === rule.mode)}
			onChange={value => set({ mode: value.value })}
			readOnly
			dropdownStyle={{ width: '20vw', optionWidth: '20vw', labelUp: true }}
		/>
		{rule.mode !== 'off' && <>
			<ScopeCloud
				label={translateToString('opening_hours_rule.event_types')}
				none={translateToString('opening_hours_rule.all_event_types')}
				tags={eventTypeTags}
				selected={rule.event_type_scope}
				onChange={event_type_scope => set({ event_type_scope })}
			/>
			<ScopeCloud
				label={translateToString('opening_hours_rule.team_scope')}
				none='-'
				tags={teams}
				selected={rule.team_scope}
				onChange={team_scope => set({ team_scope })}
			/>
			<ScopeCloud
				label={translateToString('opening_hours_rule.scope')}
				none={rule.team_scope ? '-' : translateToString('all_users')}
				tags={activeUsers}
				selected={rule.user_scope}
				onChange={user_scope => set({ user_scope })}
			/>
		</>}
	</FlexDiv>;
}
